import { useState } from 'react';
import logoPHSchool from './assets/logo/logo_PHSchool.png';
import './LoginPage.css';

const translations = {
  en: {
    backToHome: 'Back to Homepage',
    kicker: 'Official Portal Access',
    title: 'Sign In to Your Account',
    subtitle: 'Enter your registered email and password to access your admissions portal.',
    showcaseBadge: 'Unified School Portal',
    showcaseHeading: 'Welcome to Permata Harapan Admissions Desk',
    showcaseDesc: 'Sign in to track your child\'s admissions status, choose education levels, or access academic administrative dashboards.',
    perks: [
      'Manage multiple student enrollments from one parent desk',
      'Select and monitor all levels: Preschool, TK, SD, SMP, SMA',
      'Official encrypted portal with verified security protocols',
    ],
    trustText: 'Official School Portal with End-to-End Data Encryption',
    emailLabel: 'Email Address',
    emailPlaceholder: 'name@email.com',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    signInButton: 'Sign In to Portal',
    signingInButton: 'Signing In...',
    registerPrompt: 'Don\'t have a parent account yet?',
    registerLink: 'Create Parent Account',
    errorTitle: 'Unable to Sign In',
    fillAllFields: 'Please fill in both email and password fields',
    invalidEmail: 'Please enter a valid email address',
    passwordCaseHint: 'Incorrect password. Note: Passwords are case-sensitive (please check if Caps Lock is active).',
    genericError: 'Email or password does not match our records. Please verify and try again.',
    networkError: 'Network error. Please check your internet connection and try again.',
  },
  id: {
    backToHome: 'Kembali ke Beranda',
    kicker: 'Portal Masuk Resmi',
    title: 'Masuk ke Akun Anda',
    subtitle: 'Masukkan alamat email dan kata sandi yang telah terdaftar untuk melanjutkan.',
    showcaseBadge: 'Portal Terpadu Sekolah',
    showcaseHeading: 'Selamat Datang di Dasbor Penerimaan Siswa',
    showcaseDesc: 'Masuk untuk memantau status pendaftaran anak, memilih jenjang pendidikan baru, atau mengakses dasbor administrasi.',
    perks: [
      'Kelola pendaftaran beberapa anak sekaligus dalam 1 akun',
      'Pilih dan pantau semua jenjang: Playgroup, TK, SD, SMP, SMA',
      'Sistem terintegrasi resmi dengan keamanan data terenkripsi',
    ],
    trustText: 'Sistem Penerimaan Resmi Sekolah Terintegrasi & Terenkripsi Aman',
    emailLabel: 'Alamat Email',
    emailPlaceholder: 'nama@email.com',
    passwordLabel: 'Kata Sandi',
    passwordPlaceholder: 'Masukkan kata sandi Anda',
    signInButton: 'Masuk ke Portal',
    signingInButton: 'Sedang Masuk...',
    registerPrompt: 'Belum memiliki akun orang tua?',
    registerLink: 'Daftar Akun Baru',
    errorTitle: 'Masuk Belum Berhasil',
    fillAllFields: 'Silakan isi alamat email dan kata sandi',
    invalidEmail: 'Silakan masukkan alamat email yang valid',
    passwordCaseHint: 'Kata sandi salah. Catatan: kata sandi bersifat case-sensitive (periksa apakah tombol Caps Lock aktif).',
    genericError: 'Email atau kata sandi tidak cocok dengan data kami. Silakan periksa kembali.',
    networkError: 'Kesalahan jaringan. Silakan periksa koneksi internet Anda dan coba lagi.',
  },
};

function LoginForm({ onLoginSuccess, apiBase }) {
  const [currentLang, setCurrentLang] = useState(() => {
    try {
      return localStorage.getItem('phs-language') || 'id';
    } catch (_) {
      return 'id';
    }
  });

  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem('login_prefill_email') || '';
    } catch (_) {
      return '';
    }
  });

  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const copy = translations[currentLang] || translations.id;

  const validateEmail = (val) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(val);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'email') setEmail(value);
    if (name === 'password') setPassword(value);
    if (error) setError('');
  };

  const handleBackToHome = () => {
    window.location.href = '/';
  };

  const handleGoToRegister = () => {
    window.location.href = '/register';
  };

  async function handleLogin(e) {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError(copy.fillAllFields);
      return;
    }

    if (!validateEmail(email)) {
      setError(copy.invalidEmail);
      return;
    }

    setLoading(true);

    try {
      const cleanEmail = email.toLowerCase().trim();

      // 1. Try Parent (admissions) login first
      try {
        const parentRes = await fetch(`${apiBase}/admissions/login`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-HTTP-Method-Override': 'POST',
          },
          credentials: 'include',
          body: JSON.stringify({ email: cleanEmail, password }),
        });

        const parentData = await parentRes.json().catch(() => null);

        if (parentRes.ok && parentData?.ok) {
          const userObj = parentData.data?.user || parentData.data;
          const token = parentData.data?.auth_token || parentData.data?.token;
          if (token) {
            localStorage.setItem('ph_auth_token', token);
          }
          onLoginSuccess({
            type: 'parent',
            user: userObj,
            auth_token: token,
          });
          return;
        }

        // If parent account exists but password was wrong, provide actionable feedback
        if (parentData?.message && parentData.message.toLowerCase().includes('incorrect password')) {
          setError(copy.passwordCaseHint);
          setLoading(false);
          return;
        }

        // If deactivated
        if (parentData?.message && parentData.message.toLowerCase().includes('deactivated')) {
          setError(parentData.message);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn('[LoginForm] Parent login check:', err);
      }

      // 2. Try Staff / Admin login
      try {
        const staffRes = await fetch(`${apiBase}/auth/login`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-HTTP-Method-Override': 'POST',
          },
          credentials: 'include',
          body: JSON.stringify({ email: cleanEmail, password }),
        });

        const staffData = await staffRes.json().catch(() => null);

        if (staffRes.ok && staffData?.ok) {
          const userObj = staffData.data?.user || staffData.data;
          const token = staffData.data?.auth_token || staffData.data?.token;
          if (token) {
            localStorage.setItem('ph_auth_token', token);
          }
          onLoginSuccess({
            type: 'staff',
            user: userObj,
            auth_token: token,
          });
          return;
        }

        if (staffData?.message && !staffData.message.toLowerCase().includes('route not found')) {
          if (staffData.message.toLowerCase().includes('password')) {
            setError(copy.passwordCaseHint);
          } else {
            setError(staffData.message);
          }
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn('[LoginForm] Staff login check:', err);
      }

      // 3. Fallback generic error
      setError(copy.genericError);
    } catch (err) {
      console.error('[LoginForm] Login error:', err);
      setError(copy.networkError);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      {/* Animated Floating Light Orbs (Luminous & Light Outside Canvas) */}
      <div className="login-ambient-light" aria-hidden="true">
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-orb login-orb-3" />
      </div>

      {/* Floating Top Bar: Navigation & Language */}
      <header className="login-top-nav">
        <button
          type="button"
          className="login-nav-back-pill"
          onClick={handleBackToHome}
          aria-label={copy.backToHome}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          <span>{copy.backToHome}</span>
        </button>

        <div className="login-lang-pill">
          <button
            type="button"
            className={`login-lang-choice ${currentLang === 'id' ? 'active' : ''}`}
            onClick={() => {
              setCurrentLang('id');
              try { localStorage.setItem('phs-language', 'id'); } catch (_) {}
            }}
          >
            ID
          </button>
          <span className="login-lang-sep" aria-hidden="true">|</span>
          <button
            type="button"
            className={`login-lang-choice ${currentLang === 'en' ? 'active' : ''}`}
            onClick={() => {
              setCurrentLang('en');
              try { localStorage.setItem('phs-language', 'en'); } catch (_) {}
            }}
          >
            EN
          </button>
        </div>
      </header>

      {/* Floating Error Toast Notification */}
      {error && (
        <div className="login-toast-alert" role="alert">
          <div className="login-toast-alert-icon">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="login-toast-alert-content">
            <strong>{copy.errorTitle}</strong>
            <p>{error}</p>
          </div>
          <button
            type="button"
            className="login-toast-alert-close"
            onClick={() => setError('')}
            aria-label="Close notification"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Main Dual-Panel Institutional Card (Royal Purple & Gold Side-by-Side) */}
      <main className="login-main-wrapper">
        <div className="login-card-shell">
          {/* LEFT PANEL: Royal Purple & Gold Institutional Showcase */}
          <aside className="login-royal-showcase">
            <div className="login-royal-glow" aria-hidden="true" />

            <div className="login-royal-content">
              {/* Brand Lockup */}
              <div className="login-royal-brand">
                <div className="login-royal-logo-shield">
                  <img src={logoPHSchool} alt="Permata Harapan School" className="login-royal-logo-img" />
                </div>
                <div className="login-royal-brand-texts">
                  <span className="login-royal-school-title">Permata Harapan</span>
                  <span className="login-royal-school-subtitle">Preschool • Primary • Secondary</span>
                </div>
              </div>

              {/* Gold Kicker Badge */}
              <div className="login-gold-badge">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
                <span>{copy.showcaseBadge}</span>
              </div>

              {/* Showcase Heading & Subtitle */}
              <h2 className="login-royal-heading">{copy.showcaseHeading}</h2>
              <p className="login-royal-desc">{copy.showcaseDesc}</p>

              {/* Concise Key Benefits List */}
              <div className="login-royal-perks-block">
                {copy.perks.map((perk, idx) => (
                  <div key={idx} className="login-royal-perk-item">
                    <span className="login-gold-perk-check">
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span>{perk}</span>
                  </div>
                ))}
              </div>

              {/* Security & Official Trust Badge */}
              <div className="login-royal-trust-bar">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span>{copy.trustText}</span>
              </div>
            </div>
          </aside>

          {/* RIGHT PANEL: Crisp White Form with Royal & Gold Accents */}
          <section className="login-royal-form-panel">
            <div className="login-royal-form-content">
              {/* Form Header */}
              <div className="login-royal-form-header">
                <span className="login-royal-kicker-tag">{copy.kicker}</span>
                <h1 className="login-royal-form-title">{copy.title}</h1>
                <p className="login-royal-form-subtitle">{copy.subtitle}</p>
              </div>

              {/* Form Element */}
              <form className="login-form-element" onSubmit={handleLogin}>
                <div className="login-fields-stack">
                  {/* Email Address */}
                  <div className="login-field-unit">
                    <label className="login-field-label" htmlFor="email">
                      <span>{copy.emailLabel}</span>
                      <span className="login-field-req">*</span>
                    </label>
                    <div className="login-input-shell">
                      <span className="login-field-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                      </span>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="login-field-input"
                        value={email}
                        onChange={handleInputChange}
                        placeholder={copy.emailPlaceholder}
                        required
                        disabled={loading}
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div className="login-field-unit">
                    <label className="login-field-label" htmlFor="password">
                      <span>{copy.passwordLabel}</span>
                      <span className="login-field-req">*</span>
                    </label>
                    <div className="login-input-shell has-toggle">
                      <span className="login-field-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </span>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        className="login-field-input"
                        value={password}
                        onChange={handleInputChange}
                        placeholder={copy.passwordPlaceholder}
                        required
                        disabled={loading}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="login-toggle-pwd-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex="-1"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Submit CTA Button (Royal Purple with Warm Gold Accent) */}
                <button
                  type="submit"
                  className="login-cta-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="login-submit-spinner-ring" aria-hidden="true" />
                      <span>{copy.signingInButton}</span>
                    </>
                  ) : (
                    <>
                      <span>{copy.signInButton}</span>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </>
                  )}
                </button>

                {/* Footer Register Link */}
                <div className="login-footer-prompt">
                  <span>{copy.registerPrompt}</span>
                  <button
                    type="button"
                    className="login-link-to-register"
                    onClick={handleGoToRegister}
                  >
                    {copy.registerLink}
                  </button>
                </div>

                {/* Direct Role Credential Quick Access Helper */}
                <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px dashed #e2e8f0' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <span>Akses Cepat Pengujian Role (1-Click Fill)</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => {
                        setEmail('director@permataharapansecondary.sch.id');
                        setPassword('Director2026!');
                        if (error) setError('');
                      }}
                      style={{
                        padding: '6px 10px',
                        fontSize: 11,
                        background: '#fef3c7',
                        border: '1px solid #fde68a',
                        borderRadius: 6,
                        color: '#92400e',
                        fontWeight: 600,
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      title="Klik untuk mengisi akun Direktur Sekolah"
                    >
                      <div style={{ fontWeight: 700 }}>Direktur Sekolah</div>
                      <div style={{ fontSize: 10, color: '#b45309', opacity: 0.85 }}>Approval Periode &amp; Biaya</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setEmail('principal@permataharapan.sch.id');
                        setPassword('Principal2026!');
                        if (error) setError('');
                      }}
                      style={{
                        padding: '6px 10px',
                        fontSize: 11,
                        background: '#f3e8ff',
                        border: '1px solid #e9d5ff',
                        borderRadius: 6,
                        color: '#6b21a8',
                        fontWeight: 600,
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      title="Klik untuk mengisi akun Kepala Sekolah"
                    >
                      <div style={{ fontWeight: 700 }}>Kepala Sekolah</div>
                      <div style={{ fontSize: 10, color: '#7e22ce', opacity: 0.85 }}>Approval Sesi Tes &amp; CMS</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setEmail('admissions@permataharapan.sch.id');
                        setPassword('Admissions2026!');
                        if (error) setError('');
                      }}
                      style={{
                        padding: '6px 10px',
                        fontSize: 11,
                        background: '#ecfdf5',
                        border: '1px solid #a7f3d0',
                        borderRadius: 6,
                        color: '#065f46',
                        fontWeight: 600,
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      title="Klik untuk mengisi akun Admin PPDB"
                    >
                      <div style={{ fontWeight: 700 }}>Admin Admissions</div>
                      <div style={{ fontSize: 10, color: '#047857', opacity: 0.85 }}>Kelola Pendaftar Siswa</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setEmail('superadmin@permataharapan.sch.id');
                        setPassword('SuperAdmin2026!');
                        if (error) setError('');
                      }}
                      style={{
                        padding: '6px 10px',
                        fontSize: 11,
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: 6,
                        color: '#334155',
                        fontWeight: 600,
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      title="Klik untuk mengisi akun Super Admin"
                    >
                      <div style={{ fontWeight: 700 }}>Super Admin</div>
                      <div style={{ fontSize: 10, color: '#475569', opacity: 0.85 }}>Master Akun &amp; Audit</div>
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

export default LoginForm;
